import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/ProfileForm.jsx
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Alert,
    Divider,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    InputAdornment,
    CircularProgress,
    Chip,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
    Star as StarIcon,
    Speed as SpeedIcon,
    Thermostat as TempIcon,
    Compress as PressureIcon,
    HelpOutline as HelpOutlineIcon,
    SignalWifiStatusbarConnectedNoInternet4 as NoReportIcon
} from '@mui/icons-material';
import { api, profileUtils } from '../../services/api';

// Texto de ayuda para "Presión Ideal en Frío" — explica el concepto TPMS al usuario.
const COLD_PSI_HELP = i18n.t('hardcoded.components_alarmprofiles_profileform.la_presion_en_frio_es');

// Severities: keep internal enum values (alarm/critical/extreme) for backend compatibility,
// but display new labels per UX requirements ("Alarma" → "Alerta" donde corresponda).
const SEVERITIES = ['alarm', 'critical', 'extreme'];
const SEVERITY_LABELS = {
    alarm: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_atencion'),
    critical: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_critica'),
    extreme: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_extrema')
};

const parseNumber = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

// Compute resulting trigger value for a threshold given base (min/max), mode and value.
const computeTrigger = (base, mode, val, side) => {
    if (base == null || val == null || isNaN(Number(val))) return null;
    const n = Number(val);
    const baseNum = Number(base);
    if (mode === 'percent') {
        if (side === 'low') return +(baseNum - (baseNum * n) / 100).toFixed(2);
        return +(baseNum + (baseNum * n) / 100).toFixed(2);
    }
    // absolute value
    if (side === 'low') return +(baseNum - n).toFixed(2);
    return +(baseNum + n).toFixed(2);
};

const ProfileForm = ({
    open,
    onClose,
    profileForm,
    tpmsSettings,
    globalMetrics,
    axleMetrics,
    previewConfigurations,
    selectedProfile,
    creating,
    updating,
    onFormChange,
    onTpmsSettingChange,
    onGlobalMetricChange,
    onAxleMetricChange,
    onSubmit
}) => {
  const { t } = useTranslation();
    const resolvedTpmsSettings = tpmsSettings || profileUtils.getDefaultTPMSSettings();

    const getMetricIcon = (metricType) => {
        switch (metricType) {
            case 'PSI': return <PressureIcon />;
            case 'TEMP': return <TempIcon />;
            case 'ACC': return <SpeedIcon />;
            default: return null;
        }
    };

    const getMetricName = (metricType) => {
        switch (metricType) {
            case 'PSI': return t('hardcoded.components_alarmprofiles_profilecard.presion');
            case 'TEMP': return t('hardcoded.components_alarmprofiles_profilecard.temperatura');
            case 'ACC': return t('hardcoded.components_alarmprofiles_profileform.acelerometro');
            default: return metricType;
        }
    };

    const formatUnitLabel = (metricType, unit) => {
        if (metricType === 'TEMP') {
            if (unit === 'C' || unit === '°C') return '°C';
            if (unit === 'F' || unit === '°F') return '°F';
        }
        return unit;
    };

    // Live preview using min/max base (no longer "ideal" for alarms; ideal kept only for pressure as informational reference)
    const renderLivePreview = ({ metricType, normalized }) => {
        const unit = formatUnitLabel(metricType, normalized.unit);
        const min = normalized.threshold_min;
        const max = normalized.threshold_max;
        const adv = normalized.advanced_settings || {};

        if (metricType === 'PSI') {
            const closeMode = adv.pressure?.close_range_mode || t('hardcoded.components_alarmprofiles_profileform.percent');
            const closeLow = adv.pressure?.close_range_percent?.low ?? adv.pressure?.close_range_value?.low;
            const closeHigh = adv.pressure?.close_range_percent?.high ?? adv.pressure?.close_range_value?.high;
            const closeLowResult = computeTrigger(min, closeMode, closeLow, 'low');
            const closeHighResult = computeTrigger(max, closeMode, closeHigh, 'high');

            return (
                <Stack spacing={0.5}>
                    {normalized.ideal_value != null && (
                        <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.components_alarmprofiles_profileform.presion_ideal_en_frio')}{normalized.ideal_value} {unit}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {t('hardcoded.components_alarmprofiles_profileform.minimo')}{min ?? '—'} {unit} {t('hardcoded.components_alarmprofiles_profileform.maximo')}{max ?? '—'} {unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('hardcoded.components_alarmprofiles_profiledetail.cierre_automatico_de_ticket')}{closeLowResult ?? '—'} a {closeHighResult ?? '—'} {unit}
                    </Typography>
                    {SEVERITIES.map((severity) => {
                        const sev = adv.pressure?.severity_levels?.[severity] || {};
                        const mode = sev.mode || t('hardcoded.components_alarmprofiles_profileform.percent');
                        const lowVal = mode === 'percent' ? sev.low : sev.low_value;
                        const highVal = mode === 'percent' ? sev.high : sev.high_value;
                        const lowResult = computeTrigger(min, mode, lowVal, 'low');
                        const highResult = computeTrigger(max, mode, highVal, 'high');
                        return (
                            <Typography key={severity} variant="caption" color="text.secondary">
                                {SEVERITY_LABELS[severity]}: {lowResult ?? '—'} a {highResult ?? '—'} {unit}
                            </Typography>
                        );
                    })}
                </Stack>
            );
        }

        if (metricType === 'TEMP') {
            // Temperature: only max-side thresholds (alarm when temp goes high), close = safe_below
            const safeBelow = adv.temperature?.safe_below;
            return (
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        {t('hardcoded.pages_billingplanspage.maximo')}{max ?? '—'} {unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('hardcoded.components_alarmprofiles_profiledetail.cierre_automatico_de_ticket_por')}{safeBelow ?? '—'} {unit}
                    </Typography>
                    {SEVERITIES.map((severity) => {
                        const sev = adv.temperature?.severity_levels?.[severity] || {};
                        const mode = sev.mode || 'value';
                        const highVal = mode === 'percent' ? sev.high : (sev.high_value ?? adv.temperature?.thresholds?.[severity]);
                        const result = computeTrigger(max, mode, highVal, 'high');
                        return (
                            <Typography key={severity} variant="caption" color="text.secondary">
                                {SEVERITY_LABELS[severity]}: {result ?? adv.temperature?.thresholds?.[severity] ?? '—'} {unit}
                            </Typography>
                        );
                    })}
                </Stack>
            );
        }
        return null;
    };

    const renderMetricEditor = ({ metricType, config, onChange, axleLabel }) => {
        const normalized = profileUtils.normalizeMetricConfig(metricType, config);
        const availableUnitsRaw = profileUtils.getUnitsForMetric(metricType);
        const validation = profileUtils.validateMetricConfig({
            ...normalized,
            metric_type: metricType,
            axle_number: 1
        });

        const setAdvancedValue = (path, value) => {
            const next = JSON.parse(
                JSON.stringify(normalized.advanced_settings || profileUtils.getDefaultAdvancedSettings(metricType, normalized))
            );
            let pointer = next;
            for (let index = 0; index < path.length - 1; index += 1) {
                const key = path[index];
                if (!pointer[key] || typeof pointer[key] !== 'object') pointer[key] = {};
                pointer = pointer[key];
            }
            pointer[path[path.length - 1]] = value;
            onChange('advanced_settings', next);
        };

        const closeMode = normalized.advanced_settings?.pressure?.close_range_mode || t('hardcoded.components_alarmprofiles_profileform.percent');
        const tempCloseSafeBelow = normalized.advanced_settings?.temperature?.safe_below;

        return (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                    {getMetricIcon(metricType)}
                    <Typography variant="body1" sx={{ flex: 1, fontWeight: 600 }}>
                        {getMetricName(metricType)} {axleLabel ? `· ${axleLabel}` : ''}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={normalized.enabled !== false}
                                onChange={(e) => onChange('enabled', e.target.checked)}
                            />
                        }
                        label={t('hardcoded.components_alarmprofiles_profileform.habilitar')}
                    />
                </Box>

                {normalized.enabled !== false && (
                    <Grid container spacing={1.5}>
                        {/* PSI: única referencia visible = Presión Ideal en Frío.
                            Se removieron los campos "Mínimo" y "Máximo" base — todos los
                            umbrales de severidad ahora se calculan en % respecto al ideal. */}
                        {metricType === 'PSI' && (
                            <>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={t('hardcoded.components_alarmprofiles_profileform.presion_ideal_en_frio')}
                                        value={normalized.ideal_value ?? ''}
                                        onChange={(e) => onChange('ideal_value', parseNumber(e.target.value))}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <StarIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title={COLD_PSI_HELP} arrow placement="top">
                                                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                        helperText={t('hardcoded.components_alarmprofiles_profileform.base_unica_los_de_cada')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                        <Select
                                            value={normalized.unit}
                                            onChange={(e) => onChange('unit', e.target.value)}
                                            label={t('hardcoded.components_alarmprofiles_profileform.unidad')}
                                        >
                                            {availableUnitsRaw.map((unit) => (
                                                <MenuItem key={unit} value={unit}>
                                                    {formatUnitLabel(metricType, unit)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        {/* TEMP: Mín "no aplica", Máx sigue como base para high. */}
                        {metricType === 'TEMP' && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={t('hardcoded.components_alarmprofiles_profileform.minimo')}
                                        value={normalized.threshold_min ?? ''}
                                        disabled
                                        helperText={t('hardcoded.components_alarmprofiles_profileform.no_aplica')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={t('hardcoded.pages_billingplanspage.maximo')}
                                        value={normalized.threshold_max ?? ''}
                                        onChange={(e) => onChange('threshold_max', parseNumber(e.target.value))}
                                        inputProps={{ 'data-tutorial-id': 'alarm-profile-temp-max' }}
                                        helperText={t('hardcoded.components_alarmprofiles_profileform.base_para_high')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                        <Select
                                            value={normalized.unit}
                                            onChange={(e) => onChange('unit', e.target.value)}
                                            label={t('hardcoded.components_alarmprofiles_profileform.unidad')}
                                        >
                                            {availableUnitsRaw.map((unit) => (
                                                <MenuItem key={unit} value={unit}>
                                                    {formatUnitLabel(metricType, unit)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        {metricType === 'PSI' && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        {i18n.t('hardcoded.components_alarmprofiles_profileform.umbrales')}<strong>{t('hardcoded.components_alarmprofiles_profileform.todos_los_se_calculan_respecto')}</strong>.
                                        Ej.: <em>10%</em> {t('hardcoded.components_alarmprofiles_profileform.minimo_en_alerta_atencion_significa')}</Typography>
                                </Grid>

                                {/* Cierre automático de ticket: Valor + % juntos con auto-cálculo bidireccional.
                                    Base UNIFICADA: Presión Ideal en Frío (ideal_value).
                                    Convención: usuario teclea % positivos; internamente low se
                                    almacena como negativo, high positivo. */}
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('hardcoded.components_alarmprofiles_profileform.cierre_automatico_de_ticket')}</Typography>
                                        {(() => {
                                            const idealBase = Number(normalized.ideal_value) || null;
                                            const closePct = normalized.advanced_settings?.pressure?.close_range_percent || {};
                                            const closeVal = normalized.advanced_settings?.pressure?.close_range_value || {};

                                            // Valores ABSOLUTOS — calcular desde % si no están explícitos.
                                            const fromPct = (signedPct) => (idealBase != null && signedPct != null
                                                ? Number((idealBase * (1 + signedPct / 100)).toFixed(2)) : null);
                                            const resolvedLowAbs = closeVal.low != null ? closeVal.low
                                                : (closePct.low != null ? fromPct(closePct.low) : null);
                                            const resolvedHighAbs = closeVal.high != null ? closeVal.high
                                                : (closePct.high != null ? fromPct(closePct.high) : null);

                                            // % MOSTRADOS — siempre positivos en UI; internamente low<0.
                                            const resolvedLowPct = closePct.low != null ? Math.abs(closePct.low)
                                                : (closeVal.low != null && idealBase ? Number(Math.abs(((closeVal.low - idealBase) / idealBase) * 100).toFixed(2)) : '');
                                            const resolvedHighPct = closePct.high != null ? Math.abs(closePct.high)
                                                : (closeVal.high != null && idealBase ? Number((((closeVal.high - idealBase) / idealBase) * 100).toFixed(2)) : '');

                                            const setCloseValue = (side, abs) => {
                                                let pctSigned = null;
                                                if (idealBase && abs != null) {
                                                    const raw = ((abs - idealBase) / idealBase) * 100;
                                                    // Forzamos signo: low siempre negativo, high siempre positivo.
                                                    pctSigned = side === 'low' ? -Math.abs(raw) : Math.abs(raw);
                                                    pctSigned = Number(pctSigned.toFixed(2));
                                                }
                                                setAdvancedValue(['pressure', 'close_range_value', side], abs);
                                                if (pctSigned != null) setAdvancedValue(['pressure', 'close_range_percent', side], pctSigned);
                                            };
                                            const setClosePercent = (side, userTypedPct) => {
                                                // El usuario teclea positivo siempre; aplicamos signo.
                                                const signed = side === 'low' ? -Math.abs(Number(userTypedPct || 0)) : Math.abs(Number(userTypedPct || 0));
                                                const abs = idealBase != null ? Number((idealBase * (1 + signed / 100)).toFixed(2)) : null;
                                                setAdvancedValue(['pressure', 'close_range_percent', side], signed);
                                                if (abs != null) setAdvancedValue(['pressure', 'close_range_value', side], abs);
                                            };

                                            return (
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <TextField fullWidth size="small" type="number" label={t('hardcoded.components_alarmprofiles_profileform.valor_minimo')}
                                                                value={resolvedLowAbs ?? ''}
                                                                onChange={(e) => setCloseValue('low', parseNumber(e.target.value))}
                                                            />
                                                            <TextField fullWidth size="small" type="number" label={t('hardcoded.components_alarmprofiles_profileform.minimo')}
                                                                value={resolvedLowPct ?? ''}
                                                                onChange={(e) => setClosePercent('low', parseNumber(e.target.value))}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <TextField fullWidth size="small" type="number" label={t('hardcoded.components_alarmprofiles_profileform.valor_maximo')}
                                                                value={resolvedHighAbs ?? ''}
                                                                onChange={(e) => setCloseValue('high', parseNumber(e.target.value))}
                                                            />
                                                            <TextField fullWidth size="small" type="number" label={t('hardcoded.components_alarmprofiles_profileform.maximo')}
                                                                value={resolvedHighPct ?? ''}
                                                                onChange={(e) => setClosePercent('high', parseNumber(e.target.value))}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })()}
                                    </Box>
                                </Grid>

                                {/* Severities: Valor y % juntos. Cada cambio recalcula el otro
                                    usando la Presión Ideal en Frío como ÚNICA base. Los % se
                                    interpretan así:
                                      lado MÍNIMO (low) → cuánto baja desde el ideal (se almacena con signo negativo).
                                      lado MÁXIMO (high) → cuánto sube desde el ideal (signo positivo).
                                    El usuario teclea % positivos (10, 5, 15...) y el form lo
                                    convierte al signo correcto. */}
                                {SEVERITIES.map((severity) => {
                                    const sev = normalized.advanced_settings?.pressure?.severity_levels?.[severity] || {};
                                    const idealBase = Number(normalized.ideal_value) || null;

                                    // sev.low siempre es negativo en BD. Mostramos en UI el valor absoluto.
                                    const lowPct = sev.low;       // p.ej. -10
                                    const lowAbs = sev.low_value; // p.ej. 108 (si ideal=120)
                                    const highPct = sev.high;     // p.ej. +5
                                    const highAbs = sev.high_value;

                                    // Para el campo "% mínimo" mostramos el módulo positivo.
                                    const resolvedLowPct = lowPct != null ? Math.abs(lowPct) :
                                        (lowAbs != null && idealBase ? Number(Math.abs(((lowAbs - idealBase) / idealBase) * 100).toFixed(2)) : '');
                                    const resolvedHighPct = highPct != null ? highPct :
                                        (highAbs != null && idealBase ? Number((((highAbs - idealBase) / idealBase) * 100).toFixed(2)) : '');
                                    const resolvedLowAbs = lowAbs != null ? lowAbs :
                                        (lowPct != null && idealBase ? Number((idealBase * (1 + lowPct / 100)).toFixed(2)) : null);
                                    const resolvedHighAbs = highAbs != null ? highAbs :
                                        (highPct != null && idealBase ? Number((idealBase * (1 + highPct / 100)).toFixed(2)) : null);

                                    const updatePercent = (side, userTypedPct) => {
                                        // Convertir lo que el usuario tecleó (positivo en ambos lados) al signo correcto.
                                        const signed = side === 'low' ? -Math.abs(Number(userTypedPct || 0)) : Math.abs(Number(userTypedPct || 0));
                                        const absValue = idealBase != null ? Number((idealBase * (1 + signed / 100)).toFixed(2)) : null;
                                        const next = JSON.parse(JSON.stringify(normalized.advanced_settings || profileUtils.getDefaultAdvancedSettings(metricType, normalized)));
                                        next.pressure = next.pressure || {};
                                        next.pressure.severity_levels = next.pressure.severity_levels || {};
                                        next.pressure.severity_levels[severity] = next.pressure.severity_levels[severity] || {};
                                        next.pressure.severity_levels[severity].mode = t('hardcoded.components_alarmprofiles_profileform.percent');
                                        if (side === 'low') {
                                            next.pressure.severity_levels[severity].low = signed;
                                            next.pressure.severity_levels[severity].low_value = absValue;
                                        } else {
                                            next.pressure.severity_levels[severity].high = signed;
                                            next.pressure.severity_levels[severity].high_value = absValue;
                                        }
                                        onChange('advanced_settings', next);
                                    };

                                    const updateValue = (side, absValue) => {
                                        let pctValue = null;
                                        if (idealBase && absValue != null) {
                                            pctValue = Number((((absValue - idealBase) / idealBase) * 100).toFixed(2));
                                            // En lado low forzamos signo negativo; en high positivo.
                                            if (side === 'low') pctValue = -Math.abs(pctValue);
                                            else pctValue = Math.abs(pctValue);
                                        }
                                        const next = JSON.parse(JSON.stringify(normalized.advanced_settings || profileUtils.getDefaultAdvancedSettings(metricType, normalized)));
                                        next.pressure = next.pressure || {};
                                        next.pressure.severity_levels = next.pressure.severity_levels || {};
                                        next.pressure.severity_levels[severity] = next.pressure.severity_levels[severity] || {};
                                        next.pressure.severity_levels[severity].mode = 'value';
                                        if (side === 'low') {
                                            next.pressure.severity_levels[severity].low_value = absValue;
                                            if (pctValue != null) next.pressure.severity_levels[severity].low = pctValue;
                                        } else {
                                            next.pressure.severity_levels[severity].high_value = absValue;
                                            if (pctValue != null) next.pressure.severity_levels[severity].high = pctValue;
                                        }
                                        onChange('advanced_settings', next);
                                    };

                                    return (
                                        <Grid item xs={12} key={severity}>
                                            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    {SEVERITY_LABELS[severity]}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {/* Lado MÍNIMO: caída desde el ideal en frío */}
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label={t('hardcoded.components_alarmprofiles_profileform.valor_minimo')}
                                                                value={resolvedLowAbs ?? ''}
                                                                onChange={(e) => updateValue('low', parseNumber(e.target.value))}
                                                                helperText={idealBase ? t('hardcoded.components_alarmprofiles_profileform.calculado_desde_idealbase_formatunitlabel_ideal', { idealBase: idealBase, formatUnitLabel: formatUnitLabel(metricType, normalized.unit) }) : t('hardcoded.components_alarmprofiles_profileform.define_la_presion_ideal_en')}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label={t('hardcoded.components_alarmprofiles_profileform.minimo')}
                                                                value={resolvedLowPct ?? ''}
                                                                onChange={(e) => updatePercent('low', parseNumber(e.target.value))}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                helperText={t('hardcoded.components_alarmprofiles_profileform.caida_desde_el_ideal')}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    {/* Lado MÁXIMO: aumento desde el ideal en frío */}
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label={t('hardcoded.components_alarmprofiles_profileform.valor_maximo')}
                                                                value={resolvedHighAbs ?? ''}
                                                                onChange={(e) => updateValue('high', parseNumber(e.target.value))}
                                                                helperText={idealBase ? t('hardcoded.components_alarmprofiles_profileform.calculado_desde_idealbase_formatunitlabel_ideal', { idealBase: idealBase, formatUnitLabel: formatUnitLabel(metricType, normalized.unit) }) : t('hardcoded.components_alarmprofiles_profileform.define_la_presion_ideal_en')}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label={t('hardcoded.components_alarmprofiles_profileform.maximo')}
                                                                value={resolvedHighPct ?? ''}
                                                                onChange={(e) => updatePercent('high', parseNumber(e.target.value))}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                helperText={t('hardcoded.components_alarmprofiles_profileform.aumento_desde_el_ideal')}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </>
                        )}

                        {metricType === 'TEMP' && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('hardcoded.components_alarmprofiles_profileform.umbrales_termicos')}<strong>high</strong> {t('hardcoded.components_alarmprofiles_profileform.se_calcula_respecto_al')}<strong>{t('hardcoded.pages_billingplanspage.maximo')}</strong>.
                                    </Typography>
                                </Grid>
                                {/* Cierre automático de ticket (safe_below for temp) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={t('hardcoded.components_alarmprofiles_profileform.cierre_automatico_de_ticket_por')}
                                        value={tempCloseSafeBelow ?? ''}
                                        onChange={(e) => setAdvancedValue(['temperature', 'safe_below'], parseNumber(e.target.value))}
                                        helperText={t('hardcoded.components_alarmprofiles_profileform.temperatura_por_debajo_de_la')}
                                    />
                                </Grid>

                                {/* TEMP severities: Valor y % juntos (autocálculo). */}
                                {SEVERITIES.map((severity) => {
                                    const sev = normalized.advanced_settings?.temperature?.severity_levels?.[severity] || {};
                                    const legacyVal = normalized.advanced_settings?.temperature?.thresholds?.[severity];
                                    const maxBase = Number(normalized.threshold_max) || null;
                                    const highPct = sev.high;
                                    const highAbs = sev.high_value ?? legacyVal;
                                    const resolvedAbs = highAbs ?? (highPct != null ? computeTrigger(maxBase, t('hardcoded.components_alarmprofiles_profileform.percent'), highPct, 'high') : null);
                                    const resolvedPct = highPct ?? (highAbs != null && maxBase ? Number((((highAbs - maxBase) / maxBase) * 100).toFixed(2)) : null);

                                    const updatePercent = (percentValue) => {
                                        const absValue = computeTrigger(maxBase, t('hardcoded.components_alarmprofiles_profileform.percent'), percentValue, 'high');
                                        const next = JSON.parse(JSON.stringify(normalized.advanced_settings || profileUtils.getDefaultAdvancedSettings(metricType, normalized)));
                                        next.temperature = next.temperature || {};
                                        next.temperature.severity_levels = next.temperature.severity_levels || {};
                                        next.temperature.severity_levels[severity] = next.temperature.severity_levels[severity] || {};
                                        next.temperature.severity_levels[severity].mode = t('hardcoded.components_alarmprofiles_profileform.percent');
                                        next.temperature.severity_levels[severity].high = percentValue;
                                        next.temperature.severity_levels[severity].high_value = absValue;
                                        onChange('advanced_settings', next);
                                    };

                                    const updateValue = (absValue) => {
                                        let pctValue = null;
                                        if (maxBase && absValue != null) {
                                            pctValue = Number((((absValue - maxBase) / maxBase) * 100).toFixed(2));
                                        }
                                        const next = JSON.parse(JSON.stringify(normalized.advanced_settings || profileUtils.getDefaultAdvancedSettings(metricType, normalized)));
                                        next.temperature = next.temperature || {};
                                        next.temperature.severity_levels = next.temperature.severity_levels || {};
                                        next.temperature.severity_levels[severity] = next.temperature.severity_levels[severity] || {};
                                        next.temperature.severity_levels[severity].mode = 'value';
                                        next.temperature.severity_levels[severity].high_value = absValue;
                                        if (pctValue != null) next.temperature.severity_levels[severity].high = pctValue;
                                        onChange('advanced_settings', next);
                                    };

                                    return (
                                        <Grid item xs={12} key={severity}>
                                            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>{SEVERITY_LABELS[severity]}</Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        label={t('hardcoded.components_alarmprofiles_profileform.valor_maximo')}
                                                        value={resolvedAbs ?? ''}
                                                        onChange={(e) => updateValue(parseNumber(e.target.value))}
                                                        helperText={t('hardcoded.components_alarmprofiles_profileform.max_base_maxbase_formatunitlabel', { maxBase: maxBase ?? '—', formatUnitLabel: formatUnitLabel(metricType, normalized.unit) })}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        label={t('hardcoded.components_alarmprofiles_profileform.maximo')}
                                                        value={resolvedPct ?? ''}
                                                        onChange={(e) => updatePercent(parseNumber(e.target.value))}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </>
                        )}

                        <Grid item xs={12}>
                            <Alert severity={validation.isValid ? 'success' : 'warning'} sx={{ py: 0.5 }}>
                                {validation.isValid ? t('hardcoded.components_alarmprofiles_profileform.configuracion_valida') : validation.errors[0]}
                            </Alert>
                        </Grid>
                    </Grid>
                )}
            </Box>
        );
    };

    // No-report config (sensor & receiver) — defaults nuevos: 48 / 24 horas (admin Arsenal).
    const sensorNoReport = resolvedTpmsSettings.no_report_sensor || { enabled: true, value: 48, unit: 'hours' };
    const receiverNoReport = resolvedTpmsSettings.no_report_receiver || { enabled: true, value: 24, unit: 'hours' };

    // Estado: Configuración Avanzada colapsable (cerrada por defecto, salvo error).
    const [advancedOpen, setAdvancedOpen] = useState(false);

    // Carga inicial de defaults globales para perfiles NUEVOS (no sobrescribe perfiles existentes).
    useEffect(() => {
        if (selectedProfile) return; // no tocar perfiles existentes
        let cancelled = false;
        // Algunos keys SIEMPRE deben venir frescos del backend, no respetar los
        // valores hardcoded del frontend — son configuraciones globales que el
        // admin Arsenal puede haber cambiado en "Defaults Globales TPMS".
        const FORCE_OVERWRITE_KEYS = new Set(['pressure_levels_default']);

        api.alarmProfiles
            .getProfileTemplate()
            .then((res) => {
                if (cancelled || !res?.data) return;
                const template = res.data;
                Object.entries(template).forEach(([key, value]) => {
                    if (FORCE_OVERWRITE_KEYS.has(key)) {
                        // Sobrescribir completo con lo que viene del backend (fuente de verdad).
                        onTpmsSettingChange([key], value);
                    } else if (resolvedTpmsSettings[key] == null) {
                        onTpmsSettingChange([key], value);
                    } else if (typeof value === 'object' && value && typeof resolvedTpmsSettings[key] === 'object') {
                        // Merge superficial — los valores por defecto del usuario priman sobre los del template.
                        Object.entries(value).forEach(([subKey, subVal]) => {
                            if (resolvedTpmsSettings[key]?.[subKey] == null) {
                                onTpmsSettingChange([key, subKey], subVal);
                            }
                        });
                    }
                });
            })
            .catch(() => { /* fallback silencioso, ya están los defaults locales */ });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProfile]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                'data-tutorial-id': 'alarm-profile-form-dialog',
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        {selectedProfile ? t('hardcoded.components_alarmprofiles_profileform.editar_perfil_de_alerta') : t('hardcoded.components_alarmprofiles_profileform.crear_perfil_de_alerta')}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {t('audit.detail.basicInfo')}</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                placeholder={t('hardcoded.components_alarmprofiles_profileform.nombre_del_perfil')}
                                value={profileForm.name}
                                onChange={(e) => onFormChange('name', e.target.value)}
                                required
                                inputProps={{
                                    'data-tutorial-id': 'alarm-profile-name',
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={profileForm.applies_to_all_axles}
                                        onChange={(e) => onFormChange('applies_to_all_axles', e.target.checked)}
                                    />
                                }
                                label={t('hardcoded.components_alarmprofiles_profileform.aplica_para_todos_los_ejes')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {!profileForm.applies_to_all_axles && (
                                <FormControl fullWidth>
                                    <InputLabel>{t("hardcoded.components_alarmprofiles_profileform.numero_ejes")}</InputLabel>
                                    <Select
                                        value={profileForm.axle_count}
                                        onChange={(e) => onFormChange('axle_count', e.target.value)}
                                        label={t("hardcoded.components_alarmprofiles_profileform.numero_ejes")}
                                    >
                                        {[1, 2, 3, 4, 5, 6].map((num) => (
                                            <MenuItem key={num} value={num}>
                                                {num} {i18n.t('hardcoded.components_alarmprofiles_profiledetail.ejes')}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="info">
                                {t('hardcoded.components_alarmprofiles_profiledetail.los_canales_de_notificacion_portal')}<strong> Perfil &gt; Notificaciones</strong> o
                                <strong> Empresa &gt; Usuarios &gt; Editar usuario &gt; Notificaciones</strong>.
                            </Alert>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {t('hardcoded.components_alarmprofiles_profileform.configuracion_por_metrica')}</Typography>

                    {profileForm.applies_to_all_axles ? (
                        <Box>
                            {Object.keys(globalMetrics).map((metricType) => {
                                const config = globalMetrics[metricType];
                                return (
                                    <Accordion key={metricType} defaultExpanded={config?.enabled !== false}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                                {getMetricIcon(metricType)}
                                                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                                    {getMetricName(metricType)}
                                                </Typography>
                                                <Chip size="small" label={formatUnitLabel(metricType, config?.unit || (metricType === 'PSI' ? 'PSI' : 'C'))} />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {renderMetricEditor({
                                                metricType,
                                                config,
                                                onChange: (field, value) => onGlobalMetricChange(metricType, field, value)
                                            })}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>
                    ) : (
                        <Box>
                            {Array.from({ length: profileForm.axle_count }, (_, i) => i + 1).map((axleNum) => (
                                <Accordion key={axleNum} defaultExpanded={axleNum === 1}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {i18n.t('vehicleDetail.alarms.axle')}{axleNum}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {['PSI', 'TEMP'].map((metricType) => {
                                            const key = `${axleNum}-${metricType}`;
                                            return (
                                                <Box key={key} sx={{ mb: 2 }}>
                                                    {renderMetricEditor({
                                                        metricType,
                                                        config: axleMetrics[key] || globalMetrics[metricType],
                                                        axleLabel: t('hardcoded.components_alarmprofiles_profileform.eje_axlenum', { axleNum: axleNum }),
                                                        onChange: (field, value) => onAxleMetricChange(axleNum, metricType, field, value)
                                                    })}
                                                </Box>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* ========== Configuración Avanzada (colapsable, cerrada por defecto) ========== */}
                <Accordion
                    expanded={advancedOpen}
                    onChange={(_, isExpanded) => setAdvancedOpen(isExpanded)}
                    sx={{ mt: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {t('hardcoded.components_alarmprofiles_profileform.configuracion_avanzada')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {/* --- Dual (debajo de Presión Ideal en Frío, dentro de Avanzada) --- */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                {i18n.t('hardcoded.components_alarmprofiles_profileform.dual')}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                {t('hardcoded.components_alarmprofiles_profileform.compara_llantas_hermanas_por')}<strong>promedio</strong>. Severidad base fija: <strong>{t('hardcoded.components_alarmprofiles_profileform.alerta_atencion')}</strong>.
                                Defaults globales gestionados por Arsenal.
                            </Typography>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle2">{i18n.t('hardcoded.components_alarmprofiles_profileform.habilitar_dual')}</Typography>
                                    <Switch
                                        checked={Boolean(resolvedTpmsSettings.dual_yoyo?.enabled)}
                                        onChange={(e) => onTpmsSettingChange(['dual_yoyo', 'enabled'], e.target.checked)}
                                    />
                                </Box>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_alarmprofiles_profileform.nivel_1_alerta_atencion')}
                                            value={resolvedTpmsSettings.dual_yoyo?.allowed_difference_percent ?? ''}
                                            onChange={(e) => onTpmsSettingChange(['dual_yoyo', 'allowed_difference_percent'], parseNumber(e.target.value))}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            disabled={!resolvedTpmsSettings.dual_yoyo?.enabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_alarmprofiles_profileform.nivel_2_alerta_critica')}
                                            value={resolvedTpmsSettings.dual_yoyo?.critical_difference_percent ?? ''}
                                            onChange={(e) => onTpmsSettingChange(['dual_yoyo', 'critical_difference_percent'], parseNumber(e.target.value))}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            disabled={!resolvedTpmsSettings.dual_yoyo?.enabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_alarmprofiles_profileform.nivel_3_alerta_extrema')}
                                            value={resolvedTpmsSettings.dual_yoyo?.extreme_difference_percent ?? ''}
                                            onChange={(e) => onTpmsSettingChange(['dual_yoyo', 'extreme_difference_percent'], parseNumber(e.target.value))}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            disabled={!resolvedTpmsSettings.dual_yoyo?.enabled}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* --- Alerta por falta de reporte (dentro de Configuración Avanzada) --- */}
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                {t('hardcoded.components_alarmprofiles_profileform.alerta_por_falta_de_reporte')}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                {t('hardcoded.components_alarmprofiles_profileform.si_un_sensor_o_receptor')}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <NoReportIcon color="warning" />
                                            <Typography variant="subtitle2" sx={{ flex: 1 }}>{t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte')}</Typography>
                                            <Switch
                                                size="small"
                                                checked={Boolean(sensorNoReport.enabled)}
                                                onChange={(e) => onTpmsSettingChange(['no_report_sensor', 'enabled'], e.target.checked)}
                                            />
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={t('hardcoded.components_alarmprofiles_profileform.tiempo')}
                                                    value={sensorNoReport.value ?? 48}
                                                    onChange={(e) => onTpmsSettingChange(['no_report_sensor', 'value'], parseNumber(e.target.value))}
                                                    inputProps={{ min: 1 }}
                                                    disabled={!sensorNoReport.enabled}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                                    <Select
                                                        value={sensorNoReport.unit || 'hours'}
                                                        onChange={(e) => onTpmsSettingChange(['no_report_sensor', 'unit'], e.target.value)}
                                                        label={t('hardcoded.components_alarmprofiles_profileform.unidad')}
                                                        disabled={!sensorNoReport.enabled}
                                                    >
                                                        <MenuItem value="hours">{i18n.t('hardcoded.components_alarmprofiles_profiledetail.horas')}</MenuItem>
                                                        <MenuItem value="days">{t('hardcoded.components_alarmprofiles_profiledetail.dias')}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <NoReportIcon color="warning" />
                                            <Typography variant="subtitle2" sx={{ flex: 1 }}>{t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte')}</Typography>
                                            <Switch
                                                size="small"
                                                checked={Boolean(receiverNoReport.enabled)}
                                                onChange={(e) => onTpmsSettingChange(['no_report_receiver', 'enabled'], e.target.checked)}
                                            />
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={t('hardcoded.components_alarmprofiles_profileform.tiempo')}
                                                    value={receiverNoReport.value ?? 24}
                                                    onChange={(e) => onTpmsSettingChange(['no_report_receiver', 'value'], parseNumber(e.target.value))}
                                                    inputProps={{ min: 1 }}
                                                    disabled={!receiverNoReport.enabled}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                                    <Select
                                                        value={receiverNoReport.unit || 'hours'}
                                                        onChange={(e) => onTpmsSettingChange(['no_report_receiver', 'unit'], e.target.value)}
                                                        label={t('hardcoded.components_alarmprofiles_profileform.unidad')}
                                                        disabled={!receiverNoReport.enabled}
                                                    >
                                                        <MenuItem value="hours">{i18n.t('hardcoded.components_alarmprofiles_profiledetail.horas')}</MenuItem>
                                                        <MenuItem value="days">{t('hardcoded.components_alarmprofiles_profiledetail.dias')}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} size="large">
                    {t('common.cancel')}</Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    size="large"
                    disabled={creating || updating}
                    startIcon={creating || updating ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                    {creating ? t('createUser.form.creating') : updating ? t('profile.security.updating') : selectedProfile ? t('audit.actions.UPDATE') : t('hardcoded.components_alarmprofiles_profileform.crear_perfil')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileForm;
